import { useParams, useHistory } from 'react-router-dom';
import {
  getSanitizedPathName,
  queryParamsToObject,
  serializeParams,
} from 'utils';
import useSearchQueryParams from './use-search-params';

const getParams = (search, searchParams, unset) => {
  const searchObject = { ...searchParams, ...queryParamsToObject(search) };

  return Object.keys(searchObject).reduce((acc, key) => {
    if (unset.includes(key)) {
      return acc;
    }

    return {
      ...acc,
      [key]: searchObject[key],
    };
  }, {});
};

const useCustomHistory = () => {
  const params = useParams();
  const history = useHistory();
  const { searchParams, replaceParams } = useSearchQueryParams();

  /**
   * @param {{ pathname: string, search?: string, unset?: string[] }} options
   */
  const push = ({ pathname, search = '', unset = [] }) => {
    const searchObject = getParams(search, searchParams, unset);

    history.push({
      search: serializeParams({ ...searchObject }),
      pathname: getSanitizedPathName(params, pathname),
    });
  };

  /**
   * @param {{ pathname: string, search?: string, unset?: string[] }} options
   */
  const replace = ({ pathname, search = '', unset = [] }) => {
    const searchObject = getParams(search, searchParams, unset);

    history.replace({
      search: serializeParams({ ...searchObject }),
      pathname: getSanitizedPathName(params, pathname),
    });
  };

  const removeSearchQuery = (...paths) => {
    const updatedParams = { ...searchParams };

    const newUrl = new URL(window.location.href);

    // Iterate over the paths to remove them from updatedParams
    paths.forEach((path) => {
      delete updatedParams[path];
      searchParams?.delete?.(path);
      newUrl.searchParams.delete(path);
    });

    window.history.replaceState(
      {},
      '',
      `${newUrl.pathname}${newUrl.hash}${newUrl.search}`,
    );

    replaceParams(newUrl.searchParams);
  };

  return { push, replace, removeSearchQuery };
};

export default useCustomHistory;
